import { onlineManager } from "@tanstack/react-query"
import axios from "axios"
import {
  captureRequestEpoch,
  throwIfRequestObsolete,
} from "./authRequestLifecycle"

/**
 * Network state for every API client, kept in React Query's `onlineManager`
 * so that a reconnect also triggers React Query's own refetch-on-reconnect.
 *
 * A tab reopened after a night asleep fires its requests before the network
 * is back: the token refresh and the requests get no response at all. Those
 * failures mark the app offline, a health check finds out when the network
 * is back, and the HTTP layer waits for it instead of failing, so no page
 * sees an error for an outage of a few seconds. Real outages still fail,
 * after `MAX_OFFLINE_WAIT_MS`.
 */

const API_V1_BASE_URL = (
  import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v1"
).replace(/\/api\/v2$/, "/api/v1")
// Public, unauthenticated and never touches the DB (backend `api/health.py`).
const HEALTH_URL = `${API_V1_BASE_URL}/health`

export const PROBE_INTERVAL_MS = 3_000
const PROBE_TIMEOUT_MS = 5_000
/** How long one request waits for the network before it fails. */
export const MAX_OFFLINE_WAIT_MS = 60_000
/** How often one request is resent after it got no response. */
export const MAX_NETWORK_RETRIES = 2

const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "OPTIONS"])

/** Thrown when a request gave up waiting for the network. */
export class OfflineError extends Error {
  constructor() {
    super("The network did not come back in time")
    this.name = "OfflineError"
  }
}

/**
 * True when the request got no response at all, as opposed to an error the
 * server answered with. A bare `TypeError` is not enough: only `fetch` call
 * sites know theirs came from the network (see `fetchWithConnectivity`).
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof OfflineError) return true
  if (axios.isAxiosError(error)) {
    return !error.response && error.code === "ERR_NETWORK"
  }
  // Amplify wraps a failed `fetch` as `AmplifyErrorCode.NetworkError`.
  return error instanceof Error && error.name === "NetworkError"
}

/** An abandoned request: aborted by its caller, or its session has ended.
 *  Checked by name, a jsdom DOMException is not an `Error`. */
export const isAbortError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { name?: unknown }).name === "AbortError"

const abortReason = (signal: AbortSignal | null | undefined): unknown =>
  signal?.reason ?? new DOMException("The operation was aborted.", "AbortError")

/** Throws the caller's abort reason, as `fetch` rejects with it. */
export const throwIfAborted = (
  signal: AbortSignal | null | undefined,
): void => {
  if (signal?.aborted) throw abortReason(signal)
}

/** Only these are safe to resend: a request without a response may still
 *  have reached the server. */
export const isIdempotentMethod = (method: string | undefined): boolean =>
  IDEMPOTENT_METHODS.has((method ?? "GET").toUpperCase())

let probeTimer: ReturnType<typeof setTimeout> | undefined
let probing = false
/** The health check a network failure started while online. Its verdict, not
 *  the failure, decides whether the app goes offline at all. */
let pendingCheck: Promise<boolean> | undefined

const isHealthReachable = async (): Promise<boolean> => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    // `no-cors`: any answer proves the network is back, whatever its CORS
    // headers. Only a request that never got an answer rejects.
    await fetch(HEALTH_URL, {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timeout)
  }
}

const scheduleProbe = (delayMs: number): void => {
  clearTimeout(probeTimer)
  probeTimer = setTimeout(runProbe, delayMs)
}

async function runProbe(): Promise<void> {
  probeTimer = undefined
  if (probing || onlineManager.isOnline()) return
  probing = true
  try {
    const reachable = await isHealthReachable()
    if (onlineManager.isOnline()) return
    if (reachable) {
      onlineManager.setOnline(true)
    } else {
      scheduleProbe(PROBE_INTERVAL_MS)
    }
  } finally {
    probing = false
  }
}

/**
 * A request got no response. Every request waits for the health check's
 * verdict, and the app goes offline only when the check confirms it. A failure
 * the check contradicts was the request's own (an endpoint that never answers):
 * flapping offline and back would look like a reconnect to React Query, which
 * refetches every stale query on one, the failing query included, in a loop.
 */
export const reportNetworkFailure = (): void => {
  if (!onlineManager.isOnline()) {
    // Already offline: the probe finds out when the network is back.
    if (!probing) scheduleProbe(0)
    return
  }
  pendingCheck ??= isHealthReachable().then((reachable) => {
    pendingCheck = undefined
    if (!reachable) {
      onlineManager.setOnline(false)
      scheduleProbe(PROBE_INTERVAL_MS)
    }
    return reachable
  })
}

/**
 * Resolves `true` once online with no health check pending, `false` after
 * `timeoutMs` still offline. Rejects with the caller's abort as soon as
 * `signal` aborts: a caller that cancels must not be kept waiting.
 */
export const waitUntilOnline = ({
  signal,
  timeoutMs = MAX_OFFLINE_WAIT_MS,
}: {
  signal?: AbortSignal | null
  timeoutMs?: number
} = {}): Promise<boolean> => {
  if (signal?.aborted) return Promise.reject(abortReason(signal))
  if (onlineManager.isOnline() && !pendingCheck) return Promise.resolve(true)
  return new Promise((resolve, reject) => {
    let settled = false
    const settle = () => {
      settled = true
      clearTimeout(timeout)
      unsubscribe()
      signal?.removeEventListener("abort", onAbort)
    }
    const onAbort = () => {
      settle()
      reject(abortReason(signal))
    }
    const evaluate = () => {
      if (settled) return
      // A later failure can start a new check before this one's verdict lands.
      if (pendingCheck) {
        void pendingCheck.then(evaluate)
        return
      }
      if (!onlineManager.isOnline()) return
      settle()
      resolve(true)
    }
    const timeout = setTimeout(() => {
      settle()
      resolve(false)
    }, timeoutMs)
    const unsubscribe = onlineManager.subscribe(evaluate)
    signal?.addEventListener("abort", onAbort)
    evaluate()
  })
}

/**
 * Replaces React Query's default listener, which takes the browser's `online`
 * event on trust. After sleep the browser reports online as soon as an
 * interface is up, before requests route, so `online` only triggers a check.
 */
export const installConnectivityWatch = (): void => {
  onlineManager.setEventListener((setOnline) => {
    const onOffline = () => {
      setOnline(false)
      scheduleProbe(PROBE_INTERVAL_MS)
    }
    const onOnline = () => scheduleProbe(0)
    window.addEventListener("offline", onOffline)
    window.addEventListener("online", onOnline)
    return () => {
      window.removeEventListener("offline", onOffline)
      window.removeEventListener("online", onOnline)
    }
  })
}

/**
 * `fetch` for API clients that don't go through axios: a request that got no
 * response marks the app offline, and an idempotent one is resent once the
 * network is back, unless its session ended meanwhile: its headers carry the
 * token of the user who sent it.
 */
export async function fetchWithConnectivity(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const epoch = captureRequestEpoch()
  const method =
    init?.method ?? (input instanceof Request ? input.method : undefined)
  for (let retries = 0; ; retries++) {
    try {
      return await fetch(input, init)
    } catch (error) {
      // `fetch` rejects with a TypeError when no response arrived; an abort
      // is a DOMException and is the caller's own doing.
      if (!(error instanceof TypeError) || init?.signal?.aborted) throw error
      reportNetworkFailure()
      if (!isIdempotentMethod(method) || retries >= MAX_NETWORK_RETRIES) {
        throw error
      }
      const online = await waitUntilOnline({ signal: init?.signal })
      throwIfRequestObsolete(epoch)
      if (!online) throw error
    }
  }
}
